import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Grid,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  PushPin as PinIcon,
  Visibility as ViewIcon,
  ViewModule,
  ViewList,
} from '@mui/icons-material';
import { newsAPI, News } from '../api/news.api';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const NewsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [news, setNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [category, setCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'date' | 'title' | 'views'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'HEAD_COACH';

  useEffect(() => {
    fetchNews();
  }, [page, category]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await newsAPI.getNews({
        page,
        limit: 9,
        category: category !== 'all' ? category : undefined,
        search: searchQuery || undefined,
      });
      setNews(response.news || []);
      setTotalPages(response.pagination?.pages || 1);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchNews();
  };

  const getCategoryColor = (cat: string) => {
    switch (cat) {
      case 'announcement':
        return 'primary';
      case 'event':
        return 'secondary';
      case 'achievement':
        return 'success';
      default:
        return 'default';
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getSortedNews = () => {
    return [...news].sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case 'date':
          compareValue = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'title':
          compareValue = a.title.localeCompare(b.title);
          break;
        case 'views':
          compareValue = (a.viewCount || 0) - (b.viewCount || 0);
          break;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });
  };

  const sortedNews = getSortedNews();

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          News & Announcements
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/news/create')}
          >
            Create News
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Search"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={category}
            label="Category"
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="all">All Categories</MenuItem>
            <MenuItem value="announcement">Announcements</MenuItem>
            <MenuItem value="event">Events</MenuItem>
            <MenuItem value="achievement">Achievements</MenuItem>
            <MenuItem value="general">General</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => newMode && setViewMode(newMode)}
          size="small"
        >
          <ToggleButton value="grid" aria-label="grid view">
            <ViewModule />
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewList />
          </ToggleButton>
        </ToggleButtonGroup>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            label="Sort By"
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
          >
            <MenuItem value="date">Date</MenuItem>
            <MenuItem value="title">Title</MenuItem>
            <MenuItem value="views">Views</MenuItem>
          </Select>
        </FormControl>

        <ToggleButtonGroup
          value={sortOrder}
          exclusive
          onChange={(_, newOrder) => newOrder && setSortOrder(newOrder)}
          size="small"
        >
          <ToggleButton value="asc" aria-label="ascending">
            ↑
          </ToggleButton>
          <ToggleButton value="desc" aria-label="descending">
            ↓
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : news.length === 0 ? (
        <Alert severity="info">No news articles found</Alert>
      ) : viewMode === 'grid' ? (
        <>
          <Grid container spacing={3}>
            {sortedNews.map((article) => (
              <Grid item xs={12} sm={6} md={4} key={article.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 6 },
                  }}
                  onClick={() => navigate(`/news/${article.id}`)}
                >
                  {article.imageUrl && (
                    <CardMedia
                      component="img"
                      height="200"
                      image={article.imageUrl}
                      alt={article.title}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      {article.isPinned && (
                        <Chip
                          icon={<PinIcon />}
                          label="Pinned"
                          size="small"
                          color="warning"
                        />
                      )}
                      {article.category && (
                        <Chip
                          label={article.category}
                          size="small"
                          color={getCategoryColor(article.category)}
                        />
                      )}
                      {!article.isPublished && isAdmin && (
                        <Chip label="Draft" size="small" color="default" />
                      )}
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {article.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {article.excerpt
                        ? truncateText(article.excerpt, 120)
                        : truncateText(article.content.replace(/<[^>]*>/g, ''), 120)}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(article.createdAt).toLocaleDateString()}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ViewIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {article.viewCount}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      ) : (
        <>
          <List>
            {sortedNews.map((article) => (
              <Paper key={article.id} sx={{ mb: 2 }}>
                <ListItem
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => navigate(`/news/${article.id}`)}
                >
                  <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    {article.imageUrl && (
                      <CardMedia
                        component="img"
                        sx={{ width: 120, height: 120, flexShrink: 0, borderRadius: 1 }}
                        image={article.imageUrl}
                        alt={article.title}
                      />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        {article.isPinned && (
                          <Chip
                            icon={<PinIcon />}
                            label="Pinned"
                            size="small"
                            color="warning"
                          />
                        )}
                        {article.category && (
                          <Chip
                            label={article.category}
                            size="small"
                            color={getCategoryColor(article.category)}
                          />
                        )}
                        {!article.isPublished && isAdmin && (
                          <Chip label="Draft" size="small" color="default" />
                        )}
                      </Box>
                      <Typography variant="h6" gutterBottom>
                        {article.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {article.excerpt
                          ? truncateText(article.excerpt, 200)
                          : truncateText(article.content.replace(/<[^>]*>/g, ''), 200)}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(article.createdAt).toLocaleDateString()}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ViewIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {article.viewCount}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              </Paper>
            ))}
          </List>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default NewsPage;
